/*
 * Fast Simon carousel runtime — handles both the widget integration and the
 * collection rerank fallback for Featured Products carousels.
 *
 * Two independent modes, each scanned for separately on DOMContentLoaded:
 *
 *   1. Widget mode (`[data-fastsimon-widget]`)
 *      Calls the FS SDK's `productRecommendationByWidget` for the configured
 *      widget IDs, fetches a themed product card per result via Shopify's
 *      Section Rendering API, fires `RecommendationsProductsShown` once the
 *      carousel scrolls into view (the impression Fast Simon needs to compute
 *      view-to-click and attribute revenue to the widget), and fires
 *      `RecommendationProductClicked` on click. Used by
 *      `featured-products-fastsimon-widget` and any tab in
 *      `featured-products` / `featured-products-promo` that has a Widget ID
 *      set.
 *
 *   2. Rerank mode (`[data-fastsimon-rerank]`)
 *      Asks Fast Simon's `ssr-grid` endpoint for the same collection's
 *      merchandised order, then reorders the existing server-rendered DOM
 *      cards to match. Pushes out-of-stock products down, applies
 *      personalisation / popularity. Fires `CollectionProductClicked` on
 *      click. Used as the fallback for tabs without a Widget ID.
 *
 * Depends on assets/fastsimon-sdk.js (loaded earlier in the section template)
 * for the shared SDK loader / event emitter.
 *
 * References:
 *   https://docs.fastsimon.com/sdk/docs/getting-started/upsell-and-cross-sell-recommendations
 *   https://docs.fastsimon.com/sdk/docs/extra/const  (FastSimonEventName)
 */
(function () {
  function isDebug() {
    try {
      if (localStorage.getItem('FS_FEATURED_DEBUG') === '1') return true;
    } catch (e) {}
    return /[?&]fs_debug=1\b/.test(window.location.search);
  }
  var DEBUG = isDebug();
  function makeLogger(prefix) {
    return function () {
      if (!DEBUG) return;
      var args = [prefix].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    };
  }
  var log = makeLogger('[FS widget]');
  var rerankLog = makeLogger('[FS featured]');

  // Pull the product handle out of FS's `u` field (typically /products/{handle},
  // sometimes prefixed with a locale e.g. /en-gb/products/{handle}).
  function extractHandle(url) {
    if (!url) return null;
    var m = /\/products\/([a-z0-9][a-z0-9-]*)/i.exec(url);
    return m ? m[1].toLowerCase() : null;
  }

  // Cache themed cards in localStorage with a TTL so repeat visitors reuse them
  // across tabs and sessions. Bump CARD_CACHE_VERSION whenever the card markup
  // changes incompatibly so old entries are ignored on next read. Key includes
  // country + currency so a market switch doesn't serve stale prices.
  var CARD_CACHE_VERSION = 'v5';
  var CARD_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
  var CARD_CACHE_KEY_PREFIX = 'fs-card:';

  function cardCacheKey(handle) {
    var country = (window.Shopify && window.Shopify.country) || '';
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    return CARD_CACHE_KEY_PREFIX + CARD_CACHE_VERSION + ':' + country + ':' + currency + ':' + handle;
  }

  function readCardCache(handle) {
    var key = cardCacheKey(handle);
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || typeof entry.v !== 'string' || typeof entry.t !== 'number') {
        localStorage.removeItem(key);
        return null;
      }
      if (Date.now() - entry.t > CARD_CACHE_TTL_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return entry.v;
    } catch (e) { return null; }
  }

  function writeCardCache(handle, html) {
    var key = cardCacheKey(handle);
    var payload = JSON.stringify({ t: Date.now(), v: html });
    try { localStorage.setItem(key, payload); return; }
    catch (e) { /* quota or private mode — try evicting our own entries below */ }

    try {
      // Quota exceeded. Drop every fs-card entry (any version) and retry once.
      // Walking backwards lets us removeItem() during iteration safely.
      for (var i = localStorage.length - 1; i >= 0; i--) {
        var k = localStorage.key(i);
        if (k && k.indexOf(CARD_CACHE_KEY_PREFIX) === 0) localStorage.removeItem(k);
      }
      localStorage.setItem(key, payload);
    } catch (e2) { /* still failing — silently skip */ }
  }

  // In-flight dedupe so concurrent fetches for the same handle share one
  // request — multiple widget sections on the same page often overlap.
  var INFLIGHT = Object.create(null);

  // Section Rendering API returns the card wrapped in <div class="shopify-section">,
  // which has no flex/height styles and breaks `h-full` / `mt-auto` propagation in
  // product-card.liquid. Unwrap so the card sits directly inside our flex wrapper.
  // Also eagerly assigns data-src / data-srcset to src / srcset because the theme's
  // lazyload library only scans the DOM on initial load — late-injected images
  // would otherwise stay on the transparent gif placeholder forever.
  function processCardHtml(html) {
    var holder = document.createElement('div');
    holder.innerHTML = html;

    var section = holder.querySelector('.shopify-section');
    if (section) {
      var frag = document.createDocumentFragment();
      while (section.firstChild) frag.appendChild(section.firstChild);
      section.replaceWith(frag);
    }

    var lazyImgs = holder.querySelectorAll('img.lazyload, img[data-src]');
    Array.prototype.forEach.call(lazyImgs, function (img) {
      var src = img.getAttribute('data-src');
      var srcset = img.getAttribute('data-srcset');
      if (src) img.setAttribute('src', src);
      if (srcset) img.setAttribute('srcset', srcset);
      img.classList.remove('lazyload');
    });

    return holder.innerHTML;
  }

  // Build a query string with section_id, market context, and the preview theme
  // id (if present). Without explicit market context the section renders against
  // the default market and `product.compare_at_price` can come back as 0
  // instead of nil, defeating the `{% unless ... %}hidden{% endunless %}` check
  // in product-card.liquid (compare-at line shows £0.00 on every card).
  function cardQueryString() {
    var params = ['section_id=api-product-card'];
    var country = window.Shopify && window.Shopify.country;
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (country) params.push('country=' + encodeURIComponent(country));
    if (currency) params.push('currency=' + encodeURIComponent(currency));
    var previewId;
    try { previewId = new URLSearchParams(window.location.search).get('preview_theme_id'); } catch (e) {}
    if (previewId) params.push('preview_theme_id=' + encodeURIComponent(previewId));
    return '?' + params.join('&');
  }

  // Fetches a themed product card via Shopify's Section Rendering API so cards
  // are visually identical to the existing featured-products carousel. Result is
  // cached in localStorage with a TTL so repeat visitors (cross-tab, cross-session)
  // skip the round trip. Concurrent calls for the same handle share one request.
  function fetchCardHtml(handle) {
    if (INFLIGHT[handle]) return INFLIGHT[handle];

    var cached = readCardCache(handle);
    if (cached) {
      log('cache hit', handle);
      return Promise.resolve(cached);
    }

    var url = '/products/' + encodeURIComponent(handle) + cardQueryString();
    // credentials: 'same-origin' lets Shopify's cart / locale / market cookies
    // travel with the request, so the section renders in the shopper's actual
    // market (otherwise prices, including compare_at, can be wrong).
    var p = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        delete INFLIGHT[handle];
        if (!html) return null;
        var processed = processCardHtml(html);
        writeCardCache(handle, processed);
        return processed;
      })
      .catch(function () { delete INFLIGHT[handle]; return null; });

    INFLIGHT[handle] = p;
    return p;
  }

  // Same wrapper classes as the rerank carousel so spacing and snap behaviour
  // match. See sections/featured-products.liquid.
  var CARD_WRAPPER_OPEN = '<div class="relative snap-start featured-product flex flex-col w-[calc(44%-8px)] sm:w-[calc(33.33%-11px)] lg:w-[calc(25%-22.5px)] xl:w-[calc(20%-24px)] shrink-0 max-sm:last:mr-5">';
  var CARD_WRAPPER_CLOSE = '</div>';

  function escapeHtml(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Minimal card rendered from the FS response payload. Used when the themed
  // card via Section Rendering API isn't available (e.g. the api-product-card
  // section hasn't been deployed to the theme yet, or the endpoint errors).
  // Better than an empty carousel — shoppers always see something.
  function minimalCardHtml(product) {
    var url = product.u || '#';
    var title = product.l || '';
    var img = product.t || '';
    var price = product.p;
    var compare = product.p_c;
    var hasCompare = compare != null && price != null && String(compare) !== String(price);
    return ''
      + '<a href="' + escapeHtml(url) + '" class="fastsimon-widget-card flex flex-col gap-2 w-full">'
      +   (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(title) + '" loading="lazy" class="w-full h-auto" />' : '')
      +   '<h3 class="text-sm lg:text-base">' + escapeHtml(title) + '</h3>'
      +   '<div class="flex gap-2">'
      +     (price != null ? '<span>' + escapeHtml(price) + '</span>' : '')
      +     (hasCompare ? '<s class="opacity-60">' + escapeHtml(compare) + '</s>' : '')
      +   '</div>'
      + '</a>';
  }

  // Per-card metadata kept alongside the rendered DOM so click handlers can fire
  // RecommendationProductClicked with the right widget_id + position.
  function render(container, entries, sourceProductId) {
    var inner = container.querySelector('[data-fastsimon-widget-inner]') || container;
    var valid = entries.filter(function (e) { return extractHandle(e.product.u); });
    if (!valid.length) {
      log('no product handles parsed from FS response');
      return;
    }

    return Promise.all(valid.map(function (e) {
      var handle = extractHandle(e.product.u);
      return fetchCardHtml(handle).then(function (html) {
        if (html) return html;
        log('themed card unavailable for', handle, '- using minimal fallback');
        return minimalCardHtml(e.product);
      });
    })).then(function (cardHtmls) {
      var pairs = cardHtmls
        .map(function (h, i) { return h ? { html: h, entry: valid[i] } : null; })
        .filter(Boolean);
      if (!pairs.length) {
        log('no cards rendered');
        return;
      }
      var html = pairs.map(function (p) { return CARD_WRAPPER_OPEN + p.html + CARD_WRAPPER_CLOSE; }).join('');
      inner.innerHTML = html;
      log('rendered', pairs.length, 'cards into', inner);
      var rendered = pairs.map(function (p) { return p.entry; });
      attachClickTracking(inner, rendered, sourceProductId);
      trackImpression(container, rendered, sourceProductId);
      reinitInjectedCards(inner);
      markReady(container);
    });
  }

  // Report the impression Fast Simon pairs with RecommendationProductClicked:
  // one RecommendationsProductsShown per widget, listing the product IDs that
  // were rendered, once the carousel is actually on screen. Without it the FS
  // dashboards see clicks and orders but no views, so view-to-click and the
  // widget's attributed revenue come out wrong. Fires once per container; a
  // carousel sitting in a hidden tab or below the fold reports when revealed.
  function trackImpression(container, entries, sourceProductId) {
    if (!container || container.getAttribute('data-fs-impression') === 'sent') return;

    function fire() {
      if (container.getAttribute('data-fs-impression') === 'sent') return;
      container.setAttribute('data-fs-impression', 'sent');
      var byWidget = {};
      entries.forEach(function (e) {
        if (!e || !e.product || e.product.id == null) return;
        (byWidget[e.widgetId] = byWidget[e.widgetId] || []).push(numericId(e.product.id));
      });
      Object.keys(byWidget).forEach(function (widgetId) {
        if (!byWidget[widgetId].length) return;
        var data = { products: byWidget[widgetId], widget_id: widgetId };
        if (sourceProductId) data.id = String(sourceProductId);
        window.fastSimonSdk.emit('RecommendationsProductsShown', data);
        log('RecommendationsProductsShown fired', data);
      });
    }

    if (!('IntersectionObserver' in window)) { fire(); return; }
    var observer = new IntersectionObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].isIntersecting && records[i].intersectionRatio >= 0.25) {
          observer.disconnect();
          fire();
          return;
        }
      }
    }, { threshold: [0.25] });
    observer.observe(container);
  }

  // Reveal a `data-fs-pending` container by setting data-fs-ready, which the
  // CSS in fastsimon-config.liquid transitions opacity:0 → 1. A no-op if the
  // container isn't marked pending (e.g. the standalone widget section, which
  // uses skeleton placeholders instead).
  function markReady(el) {
    if (el && el.setAttribute) el.setAttribute('data-fs-ready', '');
  }

  // Attach a click handler per rendered card that fires RecommendationProductClicked
  // before navigation. Using `capture: true` so the event runs before any inner
  // anchor's default action, and the FS SDK's transport (sendBeacon-equivalent)
  // can post the event even if navigation is imminent.
  function attachClickTracking(inner, entries, sourceProductId) {
    var cards = inner.children;
    for (var i = 0; i < cards.length; i++) {
      (function (card, entry) {
        if (!entry || !entry.product) return;
        card.addEventListener('click', function () {
          window.fastSimonSdk.emit('RecommendationProductClicked', {
            productID: String(entry.product.id),
            widget_id: entry.widgetId,
            position: entry.position,
            sourceProductID: sourceProductId || undefined,
          });
          log('RecommendationProductClicked fired', entry);
        }, true);
      })(cards[i], entries[i]);
    }
  }

  // Theme JS, Alpine, and the wishlist app all bind on DOMContentLoaded only,
  // so cards injected later don't get scanned. We can't enumerate every consumer,
  // so we kick the most common re-init paths: Alpine.initTree() for x-data
  // components (swatches), and a `shopify:section:load` event which Shopify-aware
  // theme/app code listens for to re-bind. Anything still inert after this needs
  // a one-line custom hook from the theme/app side.
  function reinitInjectedCards(root) {
    try {
      if (window.Alpine && typeof window.Alpine.initTree === 'function') {
        window.Alpine.initTree(root);
        log('Alpine.initTree fired on', root);
      }
    } catch (e) { log('Alpine.initTree threw', e); }

    var section = root.closest && root.closest('.shopify-section');
    var target = section || root;
    try {
      target.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: target.id ? target.id.replace('shopify-section-', '') : '' },
      }));
      log('shopify:section:load dispatched on', target);
    } catch (e) { log('shopify:section:load dispatch threw', e); }
  }

  // Coerce a Fast Simon product id to a number, for the impression event ONLY.
  // RecommendationsProductsShown reads each entry's id numerically and drops
  // entries it cannot read, so a stringified id there can silently cost an
  // impression. The click events are the opposite: ProductSeenRecommendations
  // declares productID as a string, so those stay String(). Anything
  // non-numeric is passed through untouched.
  function numericId(value) {
    if (value == null) return value;
    var n = Number(value);
    return isNaN(n) ? value : n;
  }

  // Tag each product in the response with its source widget_id and 1-based
  // position so click events can include both. The widget id comes from the
  // response item itself: Fast Simon orders the response server-side, so it
  // does not necessarily match the order of the requested widget ids. Falling
  // back to the request index keeps single-widget containers working if the
  // field is ever absent.
  function flatten(response, widgetIds) {
    var widgets = (response && response.payload) || [];
    var out = [];
    widgets.forEach(function (w, widgetIdx) {
      var widgetId = (w && (w.widgetID || w.widget_id)) || widgetIds[widgetIdx];
      var items = (w && w.payload) || [];
      items.forEach(function (item, itemIdx) {
        out.push({ product: item, widgetId: widgetId, position: itemIdx + 1 });
      });
    });
    return out;
  }

  function fillContainer(container) {
    var widgetIds = (container.getAttribute('data-widget-ids') || '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!widgetIds.length) {
      log('skip — no widget ids on', container);
      return;
    }
    var productId = container.getAttribute('data-product-id') || undefined;

    log('request', { widgetIds: widgetIds, productId: productId });
    try {
      window.FastSimonSDK.productRecommendationByWidget({
        productID: productId,
        withAttributes: false,
        widgetsIDS: widgetIds,
        callback: function (response) {
          var entries = flatten(response, widgetIds);
          log('response', entries.length, 'products');
          if (entries.length) render(container, entries, productId);
        },
      });
    } catch (e) {
      log('productRecommendationByWidget threw', e);
    }
  }

  function runWidget() {
    var containers = document.querySelectorAll('[data-fastsimon-widget]');
    if (!containers.length) return;

    if (!window.fastSimonSdk) {
      log('window.fastSimonSdk not loaded — fastsimon-sdk.js missing from section');
      return;
    }

    window.fastSimonSdk.ready()
      .then(function () {
        Array.prototype.forEach.call(containers, fillContainer);
      })
      .catch(function (err) { log('init failed', err); });
  }

  /* ------------------------------------------------------------------------
   * Rerank mode — reorders server-rendered cards by FS's ssr-grid ranking.
   * ---------------------------------------------------------------------- */

  var SSR_ENDPOINT = 'https://ssr-grid.fastsimon.com';

  function fastSimonConfig() {
    return {
      uuid: window.FAST_SIMON_UUID,
      storeId: window.FAST_SIMON_STORE_ID,
      hasMarkets: !!window.FAST_SIMON_HAS_MARKETS,
      unpublished: !!window.FAST_SIMON_UNPUBLISHED,
    };
  }

  // Mirrors fsGetUserVariation() in isp-smart-navigation.liquid.
  function getUserVariation() {
    try {
      var raw = localStorage.getItem('fast-debug-variation') || localStorage.getItem('fast-variation');
      if (!raw) return null;
      var parts = raw.split(':');
      return parts[1] || null;
    } catch (e) { return null; }
  }

  function getUserLocation(uuid) {
    if (!uuid) return null;
    try { return localStorage.getItem('isp_location_' + uuid) || null; }
    catch (e) { return null; }
  }

  function buildRerankUrl(collectionId, limit, isMobile) {
    var cfg = fastSimonConfig();
    if (!cfg.uuid || !cfg.storeId) return null;

    var url = new URL(SSR_ENDPOINT);
    var p = url.searchParams;
    p.set('uuid', cfg.uuid);
    p.set('store_id', cfg.storeId);
    p.set('category_id', collectionId);
    p.set('page', '1');
    p.set('device', isMobile ? 'mobile' : 'desktop');
    p.set('products_per_page', String(limit));

    if (cfg.hasMarkets && window.Shopify && window.Shopify.country) {
      p.set('market_context', window.Shopify.country);
      if (window.Shopify.currency && window.Shopify.currency.active) {
        p.set('active_currency', window.Shopify.currency.active);
      }
    }
    if (cfg.unpublished) p.set('unpublished', 'true');

    var variationId = getUserVariation();
    if (variationId) p.set('variation_id', variationId);

    var location = getUserLocation(cfg.uuid);
    if (location) p.set('location', location);

    return url.toString();
  }

  function parseRerankHandles(html) {
    var seen = Object.create(null);
    var handles = [];
    var re = /\/products\/([a-z0-9][a-z0-9-]*)/gi;
    var match;
    while ((match = re.exec(html)) !== null) {
      var handle = match[1].toLowerCase();
      if (!seen[handle]) {
        seen[handle] = true;
        handles.push(handle);
      }
    }
    return handles;
  }

  // Multiple sections frequently rerank the same collection — dedupe by URL.
  var INFLIGHT_RANKING = Object.create(null);

  function fetchRanking(collectionId, limit, isMobile) {
    var url = buildRerankUrl(collectionId, limit, isMobile);
    if (!url) return Promise.resolve(null);
    if (INFLIGHT_RANKING[url]) return INFLIGHT_RANKING[url];

    rerankLog('fetch', url);
    // credentials: 'omit' — FS ssr-grid serves Access-Control-Allow-Origin: *
    // (anonymous `crossorigin` on the preload in isp-ssr-preload.liquid),
    // so a credentialed request would be CORS-rejected. Visitor signals
    // are passed explicitly via query params (variation_id, location, etc.).
    var p = fetch(url, { credentials: 'omit' })
      .then(function (res) {
        rerankLog('status', res.status);
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html) return null;
        var handles = parseRerankHandles(html);
        rerankLog('parsed', handles.length, 'handles', handles);
        return handles;
      });

    INFLIGHT_RANKING[url] = p;
    return p;
  }

  function reorderCards(container, ranking) {
    var cards = Array.prototype.slice.call(
      container.querySelectorAll(':scope > [data-fastsimon-card]')
    );
    if (!cards.length) {
      rerankLog('no [data-fastsimon-card] children found in', container);
      return;
    }

    var byHandle = Object.create(null);
    cards.forEach(function (card) {
      var handle = (card.getAttribute('data-handle') || '').toLowerCase();
      if (handle && !byHandle[handle]) byHandle[handle] = card;
    });

    var ordered = [];
    var matched = 0;
    var used = Object.create(null);
    ranking.forEach(function (handle) {
      var card = byHandle[handle];
      if (card && !used[handle]) {
        ordered.push(card);
        used[handle] = true;
        matched++;
      }
    });
    cards.forEach(function (card) {
      var handle = (card.getAttribute('data-handle') || '').toLowerCase();
      if (!used[handle]) ordered.push(card);
    });

    rerankLog('matched', matched, 'of', cards.length, 'cards from FS ranking');

    var changed = false;
    for (var i = 0; i < cards.length; i++) {
      if (cards[i] !== ordered[i]) { changed = true; break; }
    }
    if (!changed) {
      rerankLog('FS order matches server-rendered order — no DOM change');
      return;
    }

    var frag = document.createDocumentFragment();
    ordered.forEach(function (card) { frag.appendChild(card); });
    container.appendChild(frag);
    rerankLog('reordered', container);
  }

  // Click tracking for rerank-mode cards. Fires CollectionProductClicked with
  // the seed collection, the card's product ID, and the post-rerank position
  // (the order shoppers see). capture: true so the event fires before the
  // anchor's default navigation.
  function attachRerankClickTracking(container, collectionId) {
    if (!window.fastSimonSdk) return;
    var cards = container.querySelectorAll(':scope > [data-fastsimon-card]');
    Array.prototype.forEach.call(cards, function (card, idx) {
      if (card.__fsClickBound) return;
      card.__fsClickBound = true;
      var productId = card.getAttribute('data-product-id');
      if (!productId) return;
      card.addEventListener('click', function () {
        window.fastSimonSdk.emit('CollectionProductClicked', {
          collectionID: String(collectionId),
          productID: String(productId),
          position: idx + 1,
        });
        rerankLog('CollectionProductClicked fired', { collectionId: collectionId, productId: productId, position: idx + 1 });
      }, true);
    });
  }

  function rerankContainer(container, isMobile) {
    var collectionId = container.getAttribute('data-fastsimon-collection-id');
    if (!collectionId) return Promise.resolve();
    var limit = parseInt(container.getAttribute('data-fastsimon-limit'), 10) || 24;

    return fetchRanking(collectionId, limit, isMobile)
      .then(function (ranking) {
        if (ranking && ranking.length) reorderCards(container, ranking);
        else rerankLog('no ranking returned for collection', collectionId);
        // Click tracking attaches regardless of whether reorder happened —
        // shoppers click the seed-collection cards either way.
        attachRerankClickTracking(container, collectionId);
        markReady(container);
      })
      .catch(function (err) {
        rerankLog('rerank failed for collection', collectionId, err);
        attachRerankClickTracking(container, collectionId);
        markReady(container);
      });
  }

  function runRerank() {
    var containers = document.querySelectorAll('[data-fastsimon-rerank]');
    if (!containers.length) return;
    var isMobile = window.matchMedia('(max-width: 520px)').matches;
    Array.prototype.forEach.call(containers, function (c) { rerankContainer(c, isMobile); });
  }

  /* ---------------------------------------------------------------------- */

  function run() {
    runWidget();
    runRerank();

    // Safety net: any [data-fs-pending] container that hasn't been marked
    // ready within 1.5s gets revealed anyway. Covers FS hangs / SDK init
    // failures / network drops so the carousel never stays invisible. On
    // the typical fast network FS finishes well under this threshold and
    // the timeout fires harmlessly on already-ready elements.
    setTimeout(function () {
      var stragglers = document.querySelectorAll('[data-fs-pending]:not([data-fs-ready])');
      Array.prototype.forEach.call(stragglers, function (el) {
        el.setAttribute('data-fs-ready', '');
        log('timeout reveal on', el);
      });
    }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
